/**
 * ProviderMovementsTab - Aba "Movimentação" do modal de detalhes do Prestador.
 *
 * Espelho de `VehicleEquipmentTab`: cards com os itens ainda em posse do prestador
 * (Kits; Rastreadores e Chips fora de kit) e o histórico na tabela oficial
 * `StockMovementsTable` com seta de direção (verde = chegou ao prestador; vermelha = saiu).
 * Fonte: `get_provider_stock_equipment` (a consulta geral de movimentações não é usada).
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { Cpu, Package, RefreshCw, Router } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard } from '@/components/layout/list-page';
import { StockMovementsTable } from '@/components/stock/StockMovementsTable';
import { useProviderEquipment } from '@/hooks/useStockMovements';
import { cn } from '@/lib/utils';

export function ProviderMovementsTab({ personId }: { personId: string }) {
  const { data, isLoading, isFetching, error, refetch } = useProviderEquipment(personId);

  if (isLoading) return <div className="p-4"><Skeleton className="h-40 w-full rounded-lg" /></div>;
  if (error) return <p className="p-4 text-sm text-muted-foreground">{(error as Error).message}</p>;

  const holding = data?.holding ?? { kits: 0, trackers: 0, chips: 0 };
  const history = data?.history ?? [];

  return (
    <div className="space-y-6 overflow-y-auto py-4">
      {/* Itens em posse do prestador */}
      <section className="grid gap-3 px-4 sm:grid-cols-3">
        <KpiCard color="purple" icon={Package} label="Total Kits" value={holding.kits} suffix="em posse" />
        <KpiCard color="blue" icon={Router} label="Rastreadores" value={holding.trackers} suffix="fora de kit" />
        <KpiCard color="cyan" icon={Cpu} label="Chips" value={holding.chips} suffix="fora de kit" />
      </section>

      {/* Histórico: tabela oficial de movimentações (mesma seção da aba Equipamentos do veículo) */}
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-3 border-b px-4 pb-3">
          <h3 className="text-sm font-semibold">Histórico <span className="font-normal text-muted-foreground">· {history.length} movimentação(ões)</span></h3>
          <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} title="Atualizar movimentações" aria-label="Atualizar movimentações">
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <StockMovementsTable items={history} isLoading={false} compact showDirection />
        </div>
      </section>
    </div>
  );
}
